/**
 * D38 -- Home widget: checklist items assigned to the logged-in user.
 * Shows top-5 pending steps across every active instance, with a "See
 * all" link into the full My Checklists page.
 */
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ListChecks, ArrowRight, CheckCircle2 } from "lucide-react";
import api from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import GlassSurface from "@/components/ui/GlassSurface";

const CATEGORY_TONES = {
  Docs:      "border-sky-500/40 bg-sky-500/10 text-sky-200",
  IT:        "border-violet-500/40 bg-violet-500/10 text-violet-200",
  Assets:    "border-emerald-500/40 bg-emerald-500/10 text-emerald-200",
  Payroll:   "border-amber-500/40 bg-amber-500/10 text-amber-200",
  Access:    "border-rose-500/40 bg-rose-500/10 text-rose-200",
  Training:  "border-fuchsia-500/40 bg-fuchsia-500/10 text-fuchsia-200",
  Other:     "border-white/20 bg-white/5 text-foreground/70",
};

export default function MyChecklistTasksWidget({ limit = 5 }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/checklists/my-tasks")
       .then((r) => setItems(r.data.items || []))
       .catch(() => setItems([]))
       .finally(() => setLoading(false));
  }, []);

  if (loading) return null;
  if (items.length === 0) return null;   // Auto-hide when nothing to do

  const preview = items.slice(0, limit);
  const overflow = Math.max(0, items.length - limit);

  return (
    <motion.div initial={{ opacity: 0, y: 4 }} animate={{ opacity: 1, y: 0 }}
                 data-testid="home-checklist-tasks">
      <GlassSurface variant="card" className="p-4 space-y-3">
        <div className="flex items-center gap-2">
          <div className="h-8 w-8 rounded-lg bg-gradient-primary flex items-center justify-center">
            <ListChecks className="h-4 w-4 text-white" />
          </div>
          <div>
            <div className="text-xs uppercase tracking-[0.16em] text-foreground/55">Checklist items for you</div>
            <div className="text-[11px] text-foreground/45">
              {items.length} pending across onboarding + offboarding
            </div>
          </div>
          <Link to="/me/checklists" className="ml-auto text-xs text-sky-300 hover:text-sky-200 flex items-center gap-1"
                  data-testid="home-checklist-see-all">
            See all <ArrowRight className="h-3 w-3" />
          </Link>
        </div>
        <div className="space-y-1">
          {preview.map((t) => (
            <Link key={`${t.instance_id}-${t.step_id}`} to={t.link}
                    data-testid={`home-checklist-item-${t.step_id}`}
                    className="flex items-center gap-2 p-2 rounded-md bg-white/5 hover:bg-white/10 transition text-xs">
              <CheckCircle2 className="h-3.5 w-3.5 text-foreground/25 shrink-0" />
              <span className="truncate flex-1">
                <span className="text-foreground/85">{t.title}</span>
                <span className="text-foreground/40 ml-1">
                  · {t.employee_name} · {t.type}
                </span>
              </span>
              <Badge variant="outline" className={`text-[9px] ${CATEGORY_TONES[t.category] || CATEGORY_TONES.Other}`}>
                {t.category}
              </Badge>
            </Link>
          ))}
          {overflow > 0 && (
            <Link to="/me/checklists" className="block text-[11px] text-foreground/50 hover:text-foreground/70 pt-1 text-center"
                    data-testid="home-checklist-overflow">
              +{overflow} more...
            </Link>
          )}
        </div>
      </GlassSurface>
    </motion.div>
  );
}
